import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import {
  ArrowLeft, Search, Download, RefreshCw, Mail, DollarSign,
  TrendingUp, Ticket as TicketIcon, CheckCircle2, AlertTriangle,
  Loader2, Receipt, CreditCard, Gift, ArrowRightLeft, Copy,
} from 'lucide-react';
import { OrderNotes, DisputeBanner, TransferDialog, CompTicketsDialog, SalesVelocityChart } from '../components/TransactionManagerTier2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUSD = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : '—';

const STATUS_STYLE = {
  completed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  refunded: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  failed: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
};

/** KPI card — compact, color-coded headline metric. */
function Kpi({ label, value, sub, color = 'indigo', icon: Icon, testid }) {
  const bg = {
    indigo: 'from-indigo-500/15 to-indigo-500/5 border-indigo-500/20',
    emerald: 'from-emerald-500/15 to-emerald-500/5 border-emerald-500/20',
    rose: 'from-rose-500/15 to-rose-500/5 border-rose-500/20',
    amber: 'from-amber-500/15 to-amber-500/5 border-amber-500/20',
    fuchsia: 'from-fuchsia-500/15 to-fuchsia-500/5 border-fuchsia-500/20',
    cyan: 'from-cyan-500/15 to-cyan-500/5 border-cyan-500/20',
  }[color];
  return (
    <Card className={`bg-gradient-to-br ${bg}`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
          <span>{label}</span>
        </div>
        <p className="text-2xl font-bold mt-1.5">{value}</p>
        {sub ? <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p> : null}
      </CardContent>
    </Card>
  );
}

export default function EventTransactionManager() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('eventism_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [refundFilter, setRefundFilter] = useState('all');
  const [compFilter, setCompFilter] = useState('all');
  const [disputeFilter, setDisputeFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [compOpen, setCompOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (statusFilter !== 'all') params.status = statusFilter;
      if (refundFilter !== 'all') params.refund_filter = refundFilter;
      if (compFilter !== 'all') params.comp_filter = compFilter;
      if (disputeFilter !== 'all') params.dispute_filter = disputeFilter;
      const r = await axios.get(`${API}/admin/events/${eventId}/transactions`, { headers, params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load transactions');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId, statusFilter, refundFilter, compFilter, disputeFilter]);
  // Manual search trigger (debounce-free, fired on Enter or button)
  const runSearch = () => load();

  const openOrder = async (order) => {
    setSelected(order);
    setDetailLoading(true);
    setDetail(null);
    try {
      const r = await axios.get(`${API}/admin/orders/${order.order_id}`, { headers });
      setDetail(r.data);
    } catch (e) {
      toast.error('Could not load order detail');
    } finally { setDetailLoading(false); }
  };

  const closeOrder = () => { setSelected(null); setDetail(null); };

  const checkPayment = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      const r = await axios.get(`${API}/tickets/confirm/${selected.order_id}`);
      if (r.data.order?.status === 'completed') {
        toast.success('Payment confirmed — tickets issued and emailed to the buyer');
      } else {
        toast.info(`Stripe has not marked this payment as paid yet (status: ${r.data.order?.status || 'pending'})`);
      }
      load();
      openOrder({ ...selected, status: r.data.order?.status || selected.status });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not verify payment');
    } finally { setActionLoading(false); }
  };

  const sendUpgradeEmail = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      const r = await axios.post(`${API}/events/${eventId}/missed-upgrades/send`,
        { order_ids: [selected.order_id], resend: true }, { headers });
      if (r.data.sent >= 1) {
        toast.success(`Upgrade email sent to ${selected.buyer_email}`);
      } else {
        toast.info('Not sent — this buyer has no missing upgrades (or no add-ons are on sale)');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send upgrade email');
    } finally { setActionLoading(false); }
  };

  const resend = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/orders/${selected.order_id}/resend-confirmation`, {}, { headers });
      toast.success(`Confirmation re-sent to ${selected.buyer_email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resend failed');
    } finally { setActionLoading(false); }
  };

  const refund = async (full) => {
    if (!selected) return;
    const remaining = (selected.gross_cents || 0) - (selected.refunded_cents || 0);
    let amount_cents = null;
    if (!full) {
      const v = prompt(`Partial refund amount in USD (max $${(remaining / 100).toFixed(2)})`);
      if (!v) return;
      amount_cents = Math.round(parseFloat(v) * 100);
      if (!amount_cents || amount_cents <= 0) { toast.error('Invalid amount'); return; }
    }
    if (!window.confirm(full ? `Issue FULL refund of ${fmtUSD(remaining)}? This will void ${selected.tickets_total} ticket(s).` : `Issue partial refund of ${fmtUSD(amount_cents)}?`)) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/orders/${selected.order_id}/refund`, full ? {} : { amount_cents }, { headers });
      toast.success('Refund issued');
      closeOrder();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    } finally { setActionLoading(false); }
  };

  const downloadCsv = () => {
    // Use the same JWT — open as a fetch so we can blob-download
    fetch(`${API}/admin/events/${eventId}/transactions/export.csv`, { headers })
      .then(r => r.ok ? r.blob() : Promise.reject(r))
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `transactions_${eventId}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success('CSV downloaded');
      })
      .catch(() => toast.error('Download failed'));
  };

  const orders = data?.orders || [];
  const kpis = data?.kpis || {};
  const settlement = data?.settlement || {};
  const evt = data?.event || {};

  const filteredOrders = useMemo(() => {
    if (!search.trim()) return orders;
    const needle = search.trim().toLowerCase();
    return orders.filter(o =>
      (o.buyer_name || '').toLowerCase().includes(needle) ||
      (o.buyer_email || '').toLowerCase().includes(needle) ||
      (o.order_id || '').toLowerCase().includes(needle) ||
      (o.promo_code || '').toLowerCase().includes(needle),
    );
  }, [orders, search]);

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="transactions-loading">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-12 pt-20" data-testid="transactions-page">
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Transaction Manager</h1>
              <p className="text-xs text-muted-foreground">
                {evt.title} {evt.event_date ? `· ${evt.event_date}` : ''}{evt.venue_name ? ` · ${evt.venue_name}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCompOpen(true)} data-testid="comp-tickets-btn"><Gift className="h-4 w-4 mr-1.5" /> Comp tickets</Button>
            <Button variant="outline" size="sm" onClick={load} data-testid="refresh-btn"><RefreshCw className="h-4 w-4 mr-1.5" /> Refresh</Button>
            <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="export-csv-btn"><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
          </div>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="kpi-strip">
          <Kpi label="Gross Revenue" value={fmtUSD(kpis.gross_cents)} sub={`${kpis.orders_count || 0} orders`} color="emerald" icon={DollarSign} testid="kpi-gross" />
          <Kpi label="Eventism Fee" value={fmtUSD(kpis.eventism_revenue_cents)} sub="Fees − Stripe" color="fuchsia" icon={DollarSign} testid="kpi-eventism-revenue" />
          <Kpi label="Organizer Payout" value={fmtUSD(kpis.net_cents)} sub="Ticket money − refunds" color="indigo" icon={TrendingUp} testid="kpi-net" />
          <Kpi label="Stripe Fees" value={fmtUSD(kpis.stripe_fee_cents)} sub="Estimated" color="amber" icon={CreditCard} testid="kpi-stripe" />
          <Kpi label="Refunds" value={fmtUSD(kpis.refund_cents)} color="rose" icon={AlertTriangle} testid="kpi-refunds" />
          <Kpi label="Tickets Sold" value={kpis.tickets_sold || 0} sub={`Avg ${fmtUSD(kpis.avg_order_cents)}/order`} color="fuchsia" icon={TicketIcon} testid="kpi-tickets" />
          <Kpi label="Check-In Rate" value={`${kpis.checkin_rate || 0}%`} sub={`${kpis.checked_in || 0} of ${kpis.tickets_sold || 0}`} color="cyan" icon={CheckCircle2} testid="kpi-checkin" />
        </div>

        {/* Settlement Summary */}
        <Card className="bg-muted/40 border-border" data-testid="settlement-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Receipt className="h-4 w-4 text-emerald-500" /> Settlement Summary
            </CardTitle>
            <p className="text-[11px] text-muted-foreground">{settlement.stripe_fee_basis || ''}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><p className="text-[11px] text-muted-foreground uppercase tracking-wider">Gross Charged</p><p className="text-lg font-semibold text-emerald-400">{fmtUSD(settlement.gross_cents)}</p></div>
              <div><p className="text-[11px] text-muted-foreground uppercase tracking-wider">− Eventism Fees</p><p className="text-lg font-semibold text-fuchsia-300">{fmtUSD(settlement.platform_fee_cents)}</p></div>
              <div><p className="text-[11px] text-muted-foreground uppercase tracking-wider">− Refunds</p><p className="text-lg font-semibold text-rose-400">{fmtUSD(settlement.refund_cents)}</p></div>
              <div className="border-l border-border pl-4"><p className="text-[11px] text-muted-foreground uppercase tracking-wider">= Organizer Payout</p><p className="text-xl font-bold text-emerald-400">{fmtUSD(settlement.payout_cents)}</p></div>
            </div>
            <div className="border-t border-border pt-3 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm" data-testid="eventism-revenue-row">
              <div><p className="text-[11px] text-muted-foreground uppercase tracking-wider">Service Fees Collected</p><p className="text-lg font-semibold text-fuchsia-300">{fmtUSD(settlement.platform_fee_cents)}</p></div>
              <div><p className="text-[11px] text-muted-foreground uppercase tracking-wider">− Stripe Fees</p><p className="text-lg font-semibold text-amber-400">{fmtUSD(settlement.stripe_fee_cents)}</p></div>
              <div className="border-l border-border pl-4 bg-fuchsia-500/10 -my-2 py-2 -mr-4 pr-4 rounded-r-lg">
                <p className="text-[11px] text-fuchsia-300 uppercase tracking-wider font-semibold">= Eventism Net Revenue</p>
                <p className="text-2xl font-bold text-fuchsia-300" data-testid="eventism-revenue-total">{fmtUSD(settlement.eventism_revenue_cents)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sales Velocity chart */}
        <SalesVelocityChart eventId={eventId} token={token} />

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[260px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runSearch()}
              placeholder="Search by name, email, order ID, promo code…"
              className="pl-9"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={refundFilter} onValueChange={setRefundFilter}>
            <SelectTrigger className="w-40" data-testid="refund-filter"><SelectValue placeholder="Refunds" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="refunded">Refunded only</SelectItem>
              <SelectItem value="not_refunded">Not refunded</SelectItem>
            </SelectContent>
          </Select>
          <Select value={compFilter} onValueChange={setCompFilter}>
            <SelectTrigger className="w-36" data-testid="comp-filter"><SelectValue placeholder="Comps" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All orders</SelectItem>
              <SelectItem value="comp_only">Comps only</SelectItem>
              <SelectItem value="paid_only">Paid only</SelectItem>
            </SelectContent>
          </Select>
          <Select value={disputeFilter} onValueChange={setDisputeFilter}>
            <SelectTrigger className="w-36" data-testid="dispute-filter"><SelectValue placeholder="Disputes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="disputed_only">Disputed only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Orders Table */}
        <Card data-testid="orders-table-card">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead className="bg-muted/40 border-b border-border">
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Order</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Buyer</th>
                  <th className="px-4 py-3 text-center">Tickets</th>
                  <th className="px-4 py-3 text-right">Gross</th>
                  <th className="px-4 py-3 text-right">Eventism</th>
                  <th className="px-4 py-3 text-right">Organizer</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Check-in</th>
                  <th className="px-4 py-3">Promo</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-12 text-muted-foreground" data-testid="no-orders">No transactions match these filters.</td></tr>
                ) : filteredOrders.map((o) => (
                  <tr
                    key={o.order_id}
                    className="border-b border-border/60 hover:bg-muted/40 cursor-pointer transition"
                    onClick={() => openOrder(o)}
                    data-testid={`order-row-${o.order_id}`}
                  >
                    <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">{o.order_id.slice(0, 14)}…</td>
                    <td className="px-4 py-3 text-xs">{fmtDate(o.created_at)}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground">{o.buyer_name || '—'}</p>
                      <p className="text-[11px] text-muted-foreground">{o.buyer_email}</p>
                    </td>
                    <td className="px-4 py-3 text-center">{o.tickets_total}</td>
                    <td className="px-4 py-3 text-right font-semibold">{fmtUSD(o.gross_cents)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-fuchsia-300" data-testid={`eventism-cell-${o.order_id}`}>{fmtUSD(o.eventism_revenue_cents)}</td>
                    <td className="px-4 py-3 text-right text-emerald-400">{fmtUSD(o.net_cents)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        <Badge className={`text-[10px] border ${STATUS_STYLE[o.status] || 'bg-slate-500/20 text-slate-300'}`}>{o.status}</Badge>
                        {o.source === 'venue_experiences' && <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 border text-[9px]" data-testid={`source-badge-${o.order_id}`}>VENUE EXP</Badge>}
                        {o.source === 'missed_upgrade_email' && <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 border text-[9px]" data-testid={`source-badge-${o.order_id}`}>UPGRADE</Badge>}
                        {o.source === 'widget' && <Badge className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 border text-[9px]">WIDGET</Badge>}
                        {o.is_comp && <Badge className="bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30 border text-[9px]">COMP</Badge>}
                        {o.is_disputed && <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/30 border text-[9px]">DISPUTE</Badge>}
                        {o.note_count > 0 && <span className="text-[10px] text-muted-foreground" title={`${o.note_count} internal note(s)`}>📝{o.note_count}</span>}
                      </div>
                      {o.is_refunded && o.status !== 'refunded' && (
                        <span className="text-[10px] text-rose-400">partial refund: {fmtUSD(o.refunded_cents)}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {o.tickets_total > 0 ? (
                        <span className={o.tickets_checked_in === o.tickets_total ? 'text-emerald-400' : 'text-muted-foreground'}>
                          {o.tickets_checked_in}/{o.tickets_total}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{o.promo_code || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Order Detail Modal */}
        <Dialog open={!!selected} onOpenChange={(open) => { if (!open) closeOrder(); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-detail-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-emerald-500" />
                Order {selected?.order_id?.slice(0, 18)}…
              </DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-2 pt-2">
                  <p className="text-sm text-foreground">{selected?.buyer_name} · <span className="text-muted-foreground">{selected?.buyer_email}</span></p>
                  {selected?.buyer_phone ? <p className="text-xs text-muted-foreground">{selected.buyer_phone}</p> : null}
                  <p className="text-[11px] text-muted-foreground">{fmtDate(selected?.created_at)}</p>
                </div>
              </DialogDescription>
            </DialogHeader>

            {detailLoading ? (
              <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
            ) : detail ? (
              <div className="space-y-5">
                {/* Chargeback banner */}
                <DisputeBanner order={detail} />

                {/* Comp ticket banner */}
                {detail.is_comp && (
                  <div className="rounded-lg border border-fuchsia-500/40 bg-fuchsia-500/10 p-3 flex items-start gap-2" data-testid="comp-banner">
                    <Gift className="h-5 w-5 text-fuchsia-500 shrink-0 mt-0.5" />
                    <div className="text-xs">
                      <p className="font-semibold text-fuchsia-300">Comp ticket order</p>
                      <p className="text-fuchsia-300/80 mt-0.5">{detail.comp_reason || 'No reason recorded.'}</p>
                    </div>
                  </div>
                )}
                {/* Items */}
                <div>
                  <h3 className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Items</h3>
                  <div className="space-y-1.5">
                    {(detail.items || []).map((it, i) => (
                      <div key={i} className="flex items-center justify-between text-sm border-b border-border/40 pb-1.5" data-testid={`order-item-${i}`}>
                        <div>
                          <p className="font-medium">{it.name} <span className="text-xs text-muted-foreground">× {it.quantity}</span></p>
                          {it.tier ? <p className="text-[11px] text-muted-foreground">{it.tier}</p> : null}
                          {it.discount_code ? <p className="text-[11px] text-emerald-400">Code {it.discount_code} applied</p> : null}
                        </div>
                        <div className="text-right">
                          {it.discount_code && (it.base_price || 0) > (it.price || 0) ? (
                            <p className="text-[11px] text-muted-foreground line-through font-mono">{fmtUSD((it.base_price || 0) * 100 * (it.quantity || 1))}</p>
                          ) : null}
                          <p className="font-mono">{fmtUSD((it.price || 0) * 100 * (it.quantity || 1))}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Fees breakdown */}
                <div>
                  <h3 className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Fees & Totals</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{fmtUSD(detail.subtotal_cents + (detail.discount_cents || 0))}</span></div>
                    {(detail.discount_cents || 0) > 0 && (
                      <div className="flex justify-between text-emerald-400" data-testid="detail-discount-line">
                        <span>Discount ({detail.discount_code}{detail.discount_tickets ? ` · ${detail.discount_tickets} ticket${detail.discount_tickets !== 1 ? 's' : ''}` : ''})</span>
                        <span className="font-mono">−{fmtUSD(detail.discount_cents)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-muted-foreground"><span>Service fee ({detail.fee_paid_by === 'buyer' ? 'buyer paid' : detail.fee_paid_by === 'organizer' ? 'organizer absorbed' : 'none'})</span><span className="font-mono">{fmtUSD(detail.service_fee_cents)}</span></div>
                    <div className="flex justify-between text-xs text-muted-foreground"><span>Estimated Stripe fee</span><span className="font-mono">−{fmtUSD(detail.stripe_fee_cents)}</span></div>
                    <div className="flex justify-between text-sm" data-testid="detail-eventism-revenue">
                      <span className="text-fuchsia-300 font-semibold">Eventism Fee</span>
                      <span className="font-mono font-bold text-fuchsia-300">{fmtUSD(detail.eventism_revenue_cents)}</span>
                    </div>
                    <div className="flex justify-between font-semibold pt-1 border-t border-border"><span>Total charged</span><span className="font-mono text-emerald-400">{fmtUSD(detail.gross_cents)}</span></div>
                    {detail.refunded_cents > 0 && <div className="flex justify-between text-xs text-rose-400"><span>Refunded</span><span className="font-mono">−{fmtUSD(detail.refunded_cents)}</span></div>}
                    <div className="flex justify-between font-bold text-sm"><span>Organizer payout</span><span className="font-mono text-emerald-400">{fmtUSD(detail.net_cents)}</span></div>
                  </div>
                </div>

                {/* Payment ref */}
                <div className="text-[11px] text-muted-foreground space-y-0.5">
                  {detail.stripe_session_id && <p>Stripe session: <span className="font-mono">{detail.stripe_session_id}</span></p>}
                  {detail.stripe_payment_intent && <p>Payment intent: <span className="font-mono">{detail.stripe_payment_intent}</span></p>}
                  {detail.promo_code && <p>Promo code: <span className="text-foreground font-medium">{detail.promo_code}</span></p>}
                </div>

                {/* Tickets list */}
                <div>
                  <h3 className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Tickets ({detail.tickets?.length || 0})</h3>
                  <div className="space-y-1">
                    {(detail.tickets || []).map((t) => (
                      <div key={t.ticket_code} className="flex items-center gap-2 text-xs border-b border-border/40 py-1.5">
                        <div className="font-mono">{t.ticket_code}</div>
                        <div className="text-muted-foreground">{t.ticket_type_name}</div>
                        {t.transferred_at && (
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 border text-[10px]" data-testid={`detail-transferred-${t.ticket_code}`}>
                            → {t.buyer_name || t.buyer_email}
                          </Badge>
                        )}
                        <div className="ml-auto">
                          {t.status === 'checked_in' || t.checked_in_at ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 border text-[10px]">checked in</Badge>
                          ) : t.status === 'refunded' ? (
                            <Badge className="bg-rose-500/20 text-rose-400 border-rose-500/30 border text-[10px]">refunded</Badge>
                          ) : (
                            <Badge className="bg-slate-500/20 text-slate-300 border-slate-500/30 border text-[10px]">{t.status || 'valid'}</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                    {(detail.items || []).some(i => i.is_addon) && (
                      <p className="text-[10px] text-indigo-300 pt-1.5" data-testid="detail-addons-holder">
                        Add-ons on this order are with {detail.addons_holder ? `${detail.addons_holder.name} (${detail.addons_holder.email})` : 'the original buyer'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-border">
                  <Button size="sm" variant="outline" onClick={resend} disabled={actionLoading} data-testid="resend-confirmation-btn">
                    <Mail className="h-3.5 w-3.5 mr-1.5" /> Resend confirmation
                  </Button>
                  {selected?.status === 'pending' && (
                    <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300" onClick={checkPayment} disabled={actionLoading} data-testid="check-payment-btn">
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Check payment &amp; complete
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="border-fuchsia-500/40 text-fuchsia-400 hover:bg-fuchsia-500/10 hover:text-fuchsia-300" onClick={sendUpgradeEmail} disabled={actionLoading} data-testid="send-upgrade-email-btn">
                    <Gift className="h-3.5 w-3.5 mr-1.5" /> Send upgrade email
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/order/${selected.order_id}/upgrade`);
                    toast.success('Upgrade link copied — text or read it to the buyer');
                  }} data-testid="copy-upgrade-link-btn">
                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy upgrade link
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setTransferOpen(true)} disabled={actionLoading} data-testid="open-transfer-btn">
                    <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" /> Transfer
                  </Button>
                  {!detail.is_refunded && detail.gross_cents > 0 && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => refund(false)} disabled={actionLoading} data-testid="refund-partial-btn">Partial refund</Button>
                      <Button size="sm" variant="destructive" onClick={() => refund(true)} disabled={actionLoading} data-testid="refund-full-btn">Full refund</Button>
                    </>
                  )}
                  <Link to={`/admin/crm`} className="ml-auto text-xs text-indigo-400 underline">View in CRM →</Link>
                </div>

                {/* Internal notes — admins only */}
                <OrderNotes
                  orderId={detail.order_id}
                  initialNotes={detail.notes || []}
                  token={token}
                  onUpdate={(next) => setDetail(prev => prev ? { ...prev, notes: next, note_count: next.length } : prev)}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-6 text-center">Unable to load order detail.</p>
            )}
          </DialogContent>
        </Dialog>

        {/* Tier-2 dialogs */}
        <CompTicketsDialog
          open={compOpen}
          onOpenChange={setCompOpen}
          eventId={eventId}
          token={token}
          onIssued={load}
        />
        <TransferDialog
          open={transferOpen}
          onOpenChange={setTransferOpen}
          order={detail}
          token={token}
          onTransferred={() => { load(); if (selected) openOrder(selected); }}
        />
      </div>
    </div>
  );
}
